import React from 'react'
import * as Sentry from '@sentry/nextjs'
import Link from 'next/link'
import styled, { css } from 'styled-components'
import SwiperCore, { Pagination } from 'swiper'
import Card, { MainCard } from './Card'
import Cta from '../styles/Cta'
import { useInView } from 'react-intersection-observer'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FadeInArea } from '../styles/Component'
import { breakpoint } from '../layout/styles'
import { useRouter } from 'next/router'
import ReactHtmlParser from 'react-html-parser'
import { ContentStackFeaturedProducts } from '../interfaces/ContentStackFeaturedProducts'
import { ContentStackSyndigoProduct } from '../interfaces/ContentStackSyndigoProduct'

SwiperCore.use([Pagination]);

const FeaturedCarousel = ({ carousel, style, path }: ContentStackFeaturedProducts) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.20 })
  const { query, locale } = useRouter()
  const [items, setItems] = React.useState(carousel.items || [])

  // Derive a stable key from item IDs so the effect only re-runs when the
  // actual product set changes, not just because a new array reference was passed.
  const carouselItemsKey = carousel.items?.map((item: ContentStackSyndigoProduct) => item.uid || item.syndigo_id).join(',')

  React.useEffect(() => {

    const syndigoItems = carousel.items.filter((item: ContentStackSyndigoProduct) => item._content_type_uid === "syndigo_product")

    if (syndigoItems.length === 0)
      return

    // AbortController cancels in-flight fetches on unmount or carousel change,
    // preventing setItems from being called on an unmounted component.
    const controller = new AbortController()
    const { signal } = controller

    const fetchItems = async () => {
      try {
        const responses = await Promise.allSettled(
          syndigoItems.map((item: any) =>
            fetch(
              `/api/syndigo/product?id=${item.syndigo_id}&locale=${locale === "fr" ? "fr-ca" : locale}&transform=true`,
              { signal }
            ).then(r => r.ok ? r.json() : Promise.reject(r.status))
          )
        )

        if (signal.aborted) return

        const validItems = responses
          .filter((result): result is PromiseFulfilledResult<any> =>
            result.status === 'fulfilled' && result.value?.[0]
          )
          .map(result => result.value[0])

        if (validItems.length > 0) {
          setItems(validItems)
        }
      } catch (error) {
        if (signal.aborted) return
        console.error('Error fetching syndigo products:', error)
        Sentry.captureException(error)
      }
    }

    fetchItems()

    return () => controller.abort()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselItemsKey, locale])
  
  const cta_color = carousel?.cta_variant || 'light';
  const type = carousel?.type || 'slider';
  return (
    <Wrapper {...{ style }} ref={ref} inView={inView} id={carousel.unique_section_id}>
      {carousel.ingredients.front_left_ingredient &&
        <Ingredient1
          src={`${carousel.ingredients.front_left_ingredient.url}?width=${230}&auto=webp&format=png`}
          srcSet={`${carousel.ingredients.front_left_ingredient.url}?width=${230}&dpr=2&auto=webp&format=png 2x`}
          alt={carousel.ingredients.front_left_ingredient.description || 'Ingredient'}
          aria-hidden="true"
          width={230}
          height={230} />}
      {carousel.ingredients.back_left_ingredient &&
        <Ingredient2
          src={`${carousel.ingredients.back_left_ingredient.url}?width=${380}&auto=webp&format=png`}
          srcSet={`${carousel.ingredients.back_left_ingredient.url}?width=${380}&dpr=2&auto=webp&format=png 2x`}
          alt={carousel.ingredients.back_left_ingredient.description || 'Ingredient'}
          aria-hidden="true"
          width={380}
          height={380} />}
      {carousel.ingredients.front_right_ingredient &&
        <Ingredient3
          src={`${carousel.ingredients.front_right_ingredient.url}?width=${285}&auto=webp&format=png`}
          srcSet={`${carousel.ingredients.front_right_ingredient.url}?width=${285}&dpr=2&auto=webp&format=png 2x`}
          alt={carousel.ingredients.front_right_ingredient.description || 'Ingredient'}
          aria-hidden="true"
          width={285}
          height={143} />}
      <Container
        pagination={{ clickable: true }}
        slidesPerView={'auto'}
        spaceBetween={24}
        centeredSlides={true}
        className={type === 'static' ? 'carouselStaticContainer' : ''}
        breakpoints={{
          768: {
            spaceBetween: 40,
            slidesPerGroup: 3,
            centeredSlides: false
          }
        }}>
        {type === 'slider' && <SwiperSlide className="fakeslide"></SwiperSlide>}
        {type === 'slider' &&
          <SwiperSlide className="featuredslide">
            <MainCard
              background_color={carousel.background_color?.[0]?.background}
              text_color={carousel.background_color?.[0]?.text_color}
            >
              <MainCardTitle fullWidth={carousel.title_full_width}>
                {ReactHtmlParser(carousel.title)}
              </MainCardTitle>
              <MainCardDescription>{ReactHtmlParser(carousel.description)}</MainCardDescription>
              {carousel.cta.href &&
                <Link href={carousel.cta.href} passHref>
                  <Cta
                    href=""
                    light={cta_color === 'light'}
                    forceLight={cta_color === 'light'}>
                    {carousel.cta.title}
                  </Cta>
                </Link>}
            </MainCard>
          </SwiperSlide>
        }
        {items?.filter((item: any) => item.url_token && item.filters?.[0]?.value && (!query.slug || query.slug.length < 2 || item.url_token !== query.slug[1])).map((item: any) =>
          <SwiperSlide key={item.uid} className="featuredslide">
            <Card {...{
              title: item.title,
              // Defense-in-depth: fallback to 'uncategorized' even though the filter
              // above already requires filters[0].value to be truthy.
              link: `/${path}/${item.filters?.[0]?.value || 'uncategorized'}/${item.url_token}`,
              uid: item.uid,
              image: item.images && item.images[0],
              path,
              color: carousel.background_color?.[0]?.color || item.color?.[0]?.background || item.product?.[0]?.color?.[0]?.background,
              code: item.product_code,
              expand_item_images: carousel.expand_item_images,
              item_style_override: carousel.item_style_override,
              on_hover_style: carousel.on_hover_style
            }} style={{ marginTop: '5rem', '--border-color': item.color?.[0]?.color || item.product?.[0]?.color?.[0]?.color }} />
          </SwiperSlide>)}
      </Container>
      {type === 'static' && carousel.cta?.href &&
        <CtaContainer className={carousel.cta_style !== undefined && `cta-style-${carousel.cta_style}`}>
          <Link passHref
                href={carousel.cta.href}>
            <StaticCta>{carousel.cta.title}</StaticCta>
          </Link>
        </CtaContainer>
      }
    </Wrapper>
  )
}

export default FeaturedCarousel

const Wrapper = styled<any>(FadeInArea)`
  padding: 0 0 6rem;
  position: relative;
  background: var(--gradient);
`

export const Container = styled(Swiper)`
  min-height: 640px;
`

const Ingredient = styled.img`
  object-fit: contain;
  object-position: center center;
  position: absolute;
  pointer-events: none;
  display: none;
  @media ${breakpoint.desktop} {
    display: inherit;
  }
`

const Ingredient1 = styled(Ingredient)`
  bottom: 110px;
  z-index: 2;
  left: -100px;
  @media ${breakpoint.desktop} {
    left: calc((50% - 560px) - 140px);
  }
`

const Ingredient2 = styled(Ingredient)`
  bottom: 140px;
  left: -180px;
  @media ${breakpoint.desktop} {
    left: calc((50% - 560px) - 210px);
  }
`

const Ingredient3 = styled(Ingredient)`
  bottom: 172px;
  right: 0;
  z-index: 2;
`

const MainCardTitle = styled.h3<any>`
  &&& {
    ${(props) => props.fullWidth && css`
    max-width: 100%;
  `}
  }
`

const MainCardDescription = styled.p<any>`
  &&& {
    span {
      white-space: nowrap;
    }
  }
`

const CtaContainer = styled.section`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  
  @media screen and ${breakpoint.mobile} {
    margin-top: 2rem;
  }
  
  @media screen and ${breakpoint.desktop} {
    margin-top: -4rem;
  }
`

const StaticCta = styled.a`
  background-color: var(--black);
  color: var(--white);
  padding: 10px 20px;
  border-radius: 2.5px;
  font-size: 16px;
  display: inline-block;
  max-width: 70vw;
  text-transform: uppercase;
  font-weight: bold;
  text-decoration: none;
  margin: 0 auto;
  position: relative;
  z-index: 99;
`